import { useQuery } from "@tanstack/react-query";
import { getSKUMovementEnquiry } from "services/data.report.service";
import { TableCard } from "ui-components/Layout";
import { ListLayout } from "ui-components/Layout";
import BasicTable from "ui-components/tables/BasicTable";
import { skuMovementSummaryEnquiryColumns } from "./columns";
import { useState } from "react";
import { useCustomerValue } from "providers/CustomerContext";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { Grid } from "@mui/material";
import { CustomDateRangePicker } from "ui-components/CustomInput";
import dayjs from "dayjs";
import { Error_, Success, Error } from "ui-components/Alert";
import { LoadingDialog } from "ui-components/Alert";
import { useTeamValue } from "providers/TeamContext";
import { EmailButton } from "ui-components/Button";
import { ExportDialog } from "ui-components/Alert";
import useCsvDownloadHook from "hooks/useCsvDownloadHook";

// date format from api: DD/MM/YYYY HH:mm:ss
const parseDateTime = (dateString) => {
  if (!dateString) return NaN;
  const [day, month, year, time = "00:00:00"] = dateString.split(/[/ ]/);
  const [hours, minutes, seconds] = time.split(":");
  return new Date(year, month - 1, day, hours, minutes, seconds).getTime();
};

const toNumber = (value) => {
  const num = Number(value);
  return Number.isNaN(num) ? 0 : num;
};

const formatQuantity = (value) => value.toLocaleString(undefined, { maximumFractionDigits: 4 });

// per-row fields that can differ across pallets; shown as distinct comma-separated values
const detailFields = [
  "cartonID",
  "zoneID",
  "locationID",
  "mln",
  "expiryDate",
  "ref1",
  "ref2",
  "category",
];

// group movement rows by sku. opening/closing balance is tracked per pallet, so take each pallet's
// earliest opening and latest closing, then sum across pallets. in/out/adjust are summed directly.
const summarizeBySku = (rows) => {
  const sorted = rows
    .map((row, index) => ({ row, index, time: parseDateTime(row.date) }))
    .sort((a, b) => {
      if (Number.isNaN(a.time) || Number.isNaN(b.time) || a.time === b.time)
        return a.index - b.index;
      return a.time - b.time;
    })
    .map((x) => x.row);

  const groups = new Map();
  sorted.forEach((row) => {
    const key = `${row.customerCode}|${row.skuCode}`;
    let group = groups.get(key);
    if (!group) {
      group = {
        customerCode: row.customerCode,
        skuCode: row.skuCode,
        skuDescriptions: row.skuDescriptions,
        inQuantity: 0,
        outQuantity: 0,
        adjustQuantity: 0,
        pallets: new Map(),
        details: Object.fromEntries(detailFields.map((f) => [f, new Set()])),
      };
      groups.set(key, group);
    }
    detailFields.forEach((f) => {
      const value = row[f] == null ? "" : String(row[f]).trim();
      if (value) group.details[f].add(value);
    });
    group.inQuantity += toNumber(row.inQuantity);
    group.outQuantity += toNumber(row.outQuantity);
    group.adjustQuantity += toNumber(row.adjustQuantity);

    const palletKey = row.palletNbr ?? "";
    const pallet = group.pallets.get(palletKey);
    if (!pallet) {
      group.pallets.set(palletKey, {
        openingQuantity: toNumber(row.openingQuantity),
        closingQuantity: toNumber(row.closingQuantity),
      });
    } else {
      pallet.closingQuantity = toNumber(row.closingQuantity);
    }
  });

  return Array.from(groups.values())
    .map(({ pallets, details, ...x }) => {
      let openingQuantity = 0;
      let closingQuantity = 0;
      pallets.forEach((p) => {
        openingQuantity += p.openingQuantity;
        closingQuantity += p.closingQuantity;
      });
      return {
        customerCode: x.customerCode,
        skuCode: x.skuCode,
        skuDescriptions: x.skuDescriptions,
        openingQuantity,
        inQuantity: x.inQuantity,
        outQuantity: x.outQuantity,
        adjustQuantity: x.adjustQuantity,
        closingQuantity,
        ...Object.fromEntries(detailFields.map((f) => [f, Array.from(details[f]).join(", ")])),
      };
    })
    .map((x) => ({
      ...x,
      openingQuantityStr: formatQuantity(x.openingQuantity),
      inQuantityStr: formatQuantity(x.inQuantity),
      outQuantityStr: formatQuantity(x.outQuantity),
      adjustQuantityStr: formatQuantity(x.adjustQuantity),
      closingQuantityStr: formatQuantity(x.closingQuantity),
    }));
};

const stripDisplayFields = (rows) =>
  rows.map((x) => {
    const {
      openingQuantityStr,
      inQuantityStr,
      outQuantityStr,
      adjustQuantityStr,
      closingQuantityStr,
      ...data
    } = x;
    return data;
  });

function SKUMovementSummaryEnquiry() {
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const [alert, setAlert] = useState(null);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const date = dayjs(new Date()).format("YYYY-MM-DD");
  const sevenDaysBefore = dayjs(new Date()).subtract(1, "day").format("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(sevenDaysBefore);
  const [endDate, setEndDate] = useState(date);
  const { downloadCsv, isDownloading } = useCsvDownloadHook();
  const { refetch, isFetching } = useQuery({
    queryKey: ["sku-movement-summary-enquiry", { customerCode }],
    queryFn: () => getSKUMovementEnquiry({ customerCode, startDate, endDate }),
    enabled: Boolean(customerCode),
    onSuccess: (data) => {
      const summary = summarizeBySku(data.data.data || []);
      setData(summary);
      setFilteredData(summary);
    },
    onError: () => {},
  });
  const exportPermission =
    allowAll ||
    (Boolean(permissions["SKU Movement_Report"]) &&
      permissions["SKU Movement_Report"].findIndex((b) => b === "export") !== -1);

  const customProps = { startDate, setStartDate, endDate, setEndDate };
  const filename = () =>
    `${customerCode}_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_SKUMovementSummary`;

  const onFetch = () => {
    setAlert(null);
    const valid =
      dayjs(endDate).isSame(dayjs(startDate)) || dayjs(endDate).isAfter(dayjs(startDate));
    if (!valid) {
      setAlert(<Error_ content="Start date cannot greater than end date" onChange={setAlert} />);
      return;
    }
    if (dayjs(date).isBefore(endDate)) {
      setAlert(<Error_ content="End date cannot greater than today date" onChange={setAlert} />);
      return;
    }
    refetch();
  };

  const handleFilterRowsChange = (filteredData) => {
    setFilteredData(filteredData);
  };

  const onSuccess = (data) => {
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const onError = (data) => {
    setAlert(<Error data={data} onChange={setAlert} />);
  };

  return (
    <ListLayout>
      {isFetching && <LoadingDialog />}
      {isDownloading && <ExportDialog />}
      {alert && <MDBox sx={{ my: 3 }}>{alert}</MDBox>}

      <TableCard sx={{ my: 3 }}>
        <MDBox margin="1rem 0 0 1rem">
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <CustomDateRangePicker {...customProps} />
            </Grid>
            <Grid item xs={3}>
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => {
                  onFetch();
                }}
              >
                search
              </MDButton>
              <MDButton
                sx={{ marginLeft: "1rem" }}
                variant="gradient"
                color="info"
                disabled={!exportPermission}
                onClick={() => {
                  downloadCsv(filename(), stripDisplayFields(filteredData));
                }}
              >
                export
              </MDButton>
            </Grid>
            <Grid item xs={5}>
              <EmailButton
                variant="gradient"
                color="info"
                filename={filename()}
                fileData={stripDisplayFields(filteredData)}
                onSuccess={onSuccess}
                onError={onError}
                customerCode={customerCode}
              />
            </Grid>
          </Grid>
        </MDBox>

        <BasicTable
          data={data}
          columns={skuMovementSummaryEnquiryColumns}
          onFilterRowsChange={handleFilterRowsChange}
        />
      </TableCard>
    </ListLayout>
  );
}

export default SKUMovementSummaryEnquiry;
